import React, { Fragment, useEffect, useState } from 'react';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import {
	FaceFrownIcon,
	MagnifyingGlassIcon,
	UserIcon,
	BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

import { classNames } from '../components/helpers/functions';

import useModal from '../hooks/useModal';

const MODAL_NAME = 'selectContacts';

let findDeep = (data, value, key = 'id') => {
	let found = null;

	const find = (d, v) =>
		d.find((o) => {
			if (o[key] === v && !found) {
				found = o;
			} else if (o.children) {
				find(o.children, v);
			}
		});

	find(data, value);
	return found;
};

const getTitle = ({ type, title, company, location }) => {
	if (type === 'person') {
		if (company && title) {
			return `${title} at ${company.companyName}`;
		} else if (company && !title) {
			return `from ${company.companyName}`;
		} else if (!company && title) {
			return title;
		}
	} else if (type === 'company') {
		return `${location && ` from ${location}`}`;
	}
	return null;
};

const sortObject = (o) =>
	Object.keys(o)
		.sort()
		.reduce((r, k) => ((r[k] = o[k]), r), {});

export default () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const [query, setQuery] = useState('');

	const contacts = open?.contacts?.rows;

	const onClose = () => {
		setClose();
	};

	const disabledIds = open?.selected?.map((s) => s.id) || [];

	const ContactItem = ({ contact, onClick }) => {
		const disabled = disabledIds?.includes(contact.id);
		return (
			<li
				key={contact.id}
				onClick={() => {
					if (!disabled) {
						setQuery('');
						onClick(contact);
					}
				}}
			>
				<div
					className={classNames(
						disabled ? 'bg-gray-100' : 'cursor-pointer hover:bg-gray-50',
						'relative flex items-center space-x-3 px-6 py-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-pink-500 '
					)}
				>
					<div className="flex-shrink-0">
						{contact?.type === 'person' && (
							<UserIcon className="h-5 w-5 text-gray-500" />
						)}
						{contact?.type === 'company' && (
							<BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
						)}
					</div>
					<div className="min-w-0 flex-1">
						<a className="focus:outline-none">
							{/* Extend touch target to entire panel */}
							<span className="absolute inset-0" aria-hidden="true" />
							<p className="text-sm font-medium text-gray-900">
								{contact?.label}
							</p>
							<p className="truncate text-xs text-gray-500">
								{getTitle(contact)}
							</p>
						</a>
					</div>
				</div>
			</li>
		);
	};

	const sorted =
		contacts
			?.filter((contact) => {
				if (query) {
					const search = query.toLowerCase();
					return (
						contact?.firstName?.toLowerCase()?.indexOf(search) > -1 ||
						contact?.lastName?.toLowerCase()?.indexOf(search) > -1 ||
						contact?.companyName?.toLowerCase()?.indexOf(search) > -1
					);
				}
				return contact;
			})
			.reduce((groups, item) => {
				const directoryLetter = (
					item?.lastName ||
					item?.firstName ||
					item?.companyName
				)
					?.substring(0, 1)
					.toUpperCase();

				return {
					...groups,
					[directoryLetter]: [...(groups[directoryLetter] || []), item],
				};
			}, {}) || [];

	return (
		<Transition.Root
			show={!!open}
			as={Fragment}
			afterLeave={() => setQuery('')}
			appear
		>
			<Dialog as="div" className="relative z-10" onClose={() => onClose()}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500/25 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="h-auto mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
							<Combobox>
								<div className="relative">
									<MagnifyingGlassIcon
										className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
										aria-hidden="true"
									/>
									<Combobox.Input
										className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 focus:outline-none sm:text-sm"
										placeholder="Search..."
										onChange={(event) => setQuery(event.target.value)}
									/>
								</div>

								<div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6">
									<div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
										<div className="ml-4 mt-2">
											<h3 className="text-lg font-medium leading-6 text-gray-900">
												All contacts
											</h3>
										</div>
									</div>
								</div>

								<div className="h-auto overflow-scroll">
									{Object.keys(sortObject(sorted)).length ? (
										Object.keys(sortObject(sorted)).map((letter) => (
											<div key={letter}>
												<div className="top-0 z-10 border-t border-b border-gray-200 bg-gray-50 px-6 py-1 text-sm font-medium text-gray-500">
													<h3>{letter}</h3>
												</div>
												<ul
													role="list"
													className="relative z-0 divide-y divide-gray-200"
												>
													{sorted[letter]
														.map((c) => {
															const label =
																c?.companyName ||
																`${c?.firstName || ''} ${c?.lastName || ''}`;

															return {
																...c,
																label,
															};
														})
														.sort((a, b) => a.label.localeCompare(b.label))
														.map((contact) => {
															return (
																<ContactItem
																	contact={contact}
																	onClick={() => {
																		typeof open?.onClick === 'function'
																			? open?.onClick(contact)
																			: {};
																	}}
																/>
															);
														})}
												</ul>
											</div>
										))
									) : (
										<div className="border-t border-gray-100 py-14 px-6 text-center text-sm sm:px-14">
											<FaceFrownIcon
												className="mx-auto h-6 w-6 text-gray-400"
												aria-hidden="true"
											/>
											<p className="mt-4 font-semibold text-gray-900">
												No contacts found
											</p>
											<p className="mt-2 text-gray-500">
												We couldn’t find a contact with that name. Please try
												again.
											</p>
										</div>
									)}
								</div>
							</Combobox>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>
	);
};
